import axios from 'axios';

const api = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, ''),
});

const shouldLogApi = import.meta.env.DEV || import.meta.env.VITE_DEBUG_API_LOGS === 'true';

api.interceptors.request.use((config) => {
  const next = { ...config };
  next.metadata = { startedAt: Date.now() };
  return next;
});

api.interceptors.response.use(
  (response) => {
    const startedAt = response.config?.metadata?.startedAt || Date.now();
    const durationMs = Date.now() - startedAt;
    if (shouldLogApi) {
      console.info('[API OK]', {
        method: response.config?.method,
        url: response.config?.url,
        status: response.status,
        durationMs,
      });
    }
    return response;
  },
  (error) => {
    const startedAt = error.config?.metadata?.startedAt || Date.now();
    const durationMs = Date.now() - startedAt;
    console.error('[API ERROR]', {
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status || null,
      durationMs,
      message: error.message,
      data: error.response?.data || null,
    });
    return Promise.reject(error);
  }
);

export async function getProperties(filters) {
  const response = await api.get('/properties', { params: filters });
  return response.data;
}

export async function getPropertyDetail(id) {
  const response = await api.get(`/properties/${id}`);
  return response.data;
}

export async function sendPropertyInquiry(id, payload) {
  const response = await api.post(`/properties/${id}/contact`, payload);
  return response.data;
}

export async function getOwnerContact() {
  const response = await api.get('/meta/owner');
  return response.data;
}

export async function getPropertyTypes() {
  const response = await api.get('/meta/property-types');
  return response.data;
}

export async function getCities() {
  const response = await api.get('/meta/cities');
  return response.data;
}

function adminHeaders(credentials) {
  return {
    'x-admin-user': credentials.username,
    'x-admin-password': credentials.password,
  };
}

export async function getAdminProperties(credentials) {
  const response = await api.get('/properties/admin/all', {
    headers: adminHeaders(credentials),
  });
  return response.data;
}

export async function getAdminInquiries(credentials) {
  const response = await api.get('/properties/admin/inquiries', {
    headers: adminHeaders(credentials),
  });
  return response.data;
}

export async function createAdminProperty(payload, credentials) {
  const response = await api.post('/properties/admin', payload, {
    headers: adminHeaders(credentials),
  });
  return response.data;
}

export async function updateAdminProperty(id, payload, credentials) {
  const response = await api.put(`/properties/admin/${id}`, payload, {
    headers: adminHeaders(credentials),
  });
  return response.data;
}

export async function deleteAdminProperty(id, credentials) {
  const response = await api.delete(`/properties/admin/${id}`, {
    headers: adminHeaders(credentials),
  });
  return response.data;
}

export async function uploadAdminPropertyImages(files, credentials) {
  const formData = new FormData();
  files.forEach((file) => formData.append('images', file));

  const response = await api.post('/properties/admin/upload-images', formData, {
    headers: adminHeaders(credentials),
  });

  return response.data?.urls || [];
}

export default api;